/* eslint-disable react/jsx-no-constructed-context-values */
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
// import { useParams } from "react-router";
import { IAppointmentTypes, IClinicData } from "../@types/diary";
import { ClinicService } from "../api/clinic.service";
import { SharedService } from "../api/shared.service";
import { AppointmentStatusService } from "../api/appointment-status.service";
import { ClinicalRecords } from "../api/clinicalRecords.service";
import { IClinicalRecordSchema } from "../@types/clinicalRecord.type";
import { PatientService } from "../api/patient.service";
import { IPatientSearchSchema } from "../@types/calendar";
import { UsersService } from "../api/users.service";

type AppContextType = {
  clinics: IClinicData[] | undefined;
  appointmentTypes: IAppointmentTypes[] | undefined;
  appointmentStatus: any[] | undefined;
  clinicalRecords: IClinicalRecordSchema[];
  patients: IPatientSearchSchema[];
  practitioners: Array<{ id: number; name: string }>; // Add this
  getAllClinics: () => void;
  getAllAppointmentTypes: () => void;
  getAllAppointmentStatus: () => void;
  getAllClinicalRecords: (id: any) => Promise<IClinicalRecordSchema[]>;
  getPatients: () => Promise<IPatientSearchSchema[]>;
  getPractitioners: () => Promise<Array<{ id: number; name: string }>>; // Add this
};

const AppContext = createContext<AppContextType>({
  clinics: [],
  appointmentTypes: [],
  appointmentStatus: [],
  clinicalRecords: [],
  patients: [],
  practitioners: [], // Add default
  getAllClinics() {},
  getAllAppointmentTypes() {},
  getAllAppointmentStatus() {},
  getAllClinicalRecords: async (id: any) => [],
  getPatients: async () => [],
  getPractitioners: async () => [], // Add default
});

export const useAppContext = () => useContext(AppContext);

type AppContextProviderProps = {
  children: ReactNode;
};

export const AppContextProvider = ({ children }: AppContextProviderProps) => {
  const [clinics, setClinics] = useState<IClinicData[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<IAppointmentTypes[]>(
    []
  );
  const [appointmentStatus, setAppointmentStatus] = useState<any[]>([]);
  const [clinicalRecords, setClinicalRecords] = useState<
    IClinicalRecordSchema[]
  >([]);
  const [patients, setPatients] = useState<IPatientSearchSchema[]>([]);
  const [practitioners, setPractitioners] = useState<
    Array<{ id: number; name: string }>
  >([]);

  const getAllClinics = async () => {
    try {
      const res = await new ClinicService().getAllClinics();
      const clinicsCalendar = res?.data?.map((item: any) => ({
        id: item.id,
        name: item?.name,
        breakStartTime: item?.breakStartTime,
        breakEndTime: item?.breakEndTime,
        color: "#008000",
        startTime: item?.startTime,
        endTime: item?.endTime,
        roomsCount: item?.roomsCount,
        appointmentsCount: item?.appointmentsCount,
      }));
      setClinics(clinicsCalendar);
    } catch (err) {
      setClinics([]);
    }
  };

  const getAllClinicalRecords = async (
    id: any
  ): Promise<IClinicalRecordSchema[]> => {
    const clinicalRecordsService = new ClinicalRecords();
    const { data: result } = await clinicalRecordsService.getAllClinicalRecords(
      id
    );

    // Check if result is valid and return it, otherwise return an empty array
    if (result && result.length > 0) {
      setClinicalRecords(result); // Set the state with fetched records
      return result; // Return the records fetched
    } else {
      console.warn("No clinical records found for ID:", id);
      setClinicalRecords([]); // Set the state to empty array if no records found
      return []; // Return an empty array
    }
  };

  const getAllAppointmentTypes = async () => {
    try {
      const { data: result } =
        await new SharedService().getAllAppointmentTypes();
      setAppointmentTypes(result.length > 0 ? result : []);
    } catch (error) {
      setAppointmentTypes([]);
    }
  };

  const getAllAppointmentStatus = async () => {
    try {
      const { data: result } =
        await new AppointmentStatusService().getAllAppointmentStatus();
      setAppointmentStatus(result.length > 0 ? result : []);
    } catch (error) {
      setAppointmentStatus([]);
    }
  };

  const getPatients = () => {
    return new PatientService()
      .getAllPatients()
      .then((res) => {
        const source = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const activeOnly = (source || []).filter((item: any) => item?.isActive !== false);
        const patientData = activeOnly.map((item: any) => ({
          id: item.id,
          name: `${item.firstName} ${item.surName}`,
          postCode: item.postCode || "",
          phone: item.phone || "",
          email: item.email || "",
        }));
        setPatients(patientData);
        return patientData;
      })
      .catch(() => {
        setPatients([]);
        return [];
      });
  };

  const getPractitioners = async (): Promise<
    Array<{ id: number; name: string }>
  > => {
    try {
      const res = await new UsersService().getAllUsers({
        roleId: "professional",
      });
      const list = (res as any)?.data ?? res;
      const filtered = (list || []).filter(
        (item: any) => String(item?.roleId || "") === "professional"
      );
      const practitionersList = filtered.map((item: any) => ({
        id: item.id,
        name: `${item.firstName} ${item.lastName || ""}`.trim(),
      }));
      setPractitioners(practitionersList);
      return practitionersList;
    } catch (error) {
      setPractitioners([]);
      return [];
    }
  };

  // Initialize all data when context is created
  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([
        getAllClinics(),
        getAllAppointmentTypes(),
        getAllAppointmentStatus(),
        getPractitioners(),
      ]);
    };

    initializeData();
  }, []);

  return (
    <AppContext.Provider
      value={{
        clinics,
        appointmentTypes,
        appointmentStatus,
        clinicalRecords,
        patients,
        practitioners,
        getAllClinics,
        getAllAppointmentTypes,
        getAllAppointmentStatus,
        getAllClinicalRecords,
        getPatients,
        getPractitioners,
      }}>
      {children}
    </AppContext.Provider>
  );
};
